/**
 * GrecaptchaV2_JS options.
 * @typedef {Object} GRECAPTCHA_OPTIONS
 * @property {String|HTMLElement} container ID selector of the container (string) or the DOM element itself.
 * @property {String} siteKey recaptcha site key - Unique identification control.
 * @property {String} theme Theme Color: ('dark','light').
 * @property {String} size The recaptcha size ('compact','normal','invisible').
 * @property {Number} tabindex  Form tabindex.
 * @property {Function} callback Executes when the user submits a successful response. The g-recaptcha-response token is passed as argument.
 * @property {Function} expiredCallback Executes when the reCAPTCHA expires and the user needs to re-verify.
 * @property {Function} errorCallback Executes when reCAPTCHA encounters an error (usually network connectivity) and cannot continue until connectivity is restored. 
 * @property {String} badge (Invisible only) position of the recaptcha. ('bottomright','bottomleft','inline').
 * @property {Boolean} isolated  (Invisible only)  If true, this reCAPTCHA instance will be part of a separate ID space.
 * @property {string|HTMLInputElement} inputForm ID selector or Input Element that receives token value after a successful response.
 */

/**
 * Google recaptcha V2.
 * @example
 * //build a checkbox captcha
 * const chkCaptchaV2 = new GrecaptchaV2_JS(grecaptcha, {
 *      container : document.querySelector('#captchaId'),
 *      siteKey : '6LdZGlUUAAAAAAi91lKwk63ltcSBkP8e8yXnv...',
 *      theme : 'light',
 *      size : 'normal',
 *      tabindex : 0, 
 *      callback : (token)=>{ ... },
 *      expiredCallback : ()=>{ ... },
 *      errorCallback : (error)=>{ ... },
 *      inputForm : document.querySelector('#inputCaptchaField')
 * });
 * 
 * //build an invisible captcha
 * const invisible_captchaV2 = new GrecaptchaV2_JS(grecaptcha, {
 *      container : document.querySelector('#captchaId'),
 *      siteKey : '6LdZGlUUAAAAAAi91lKwk63ltcSBkP8e8yXnv...',
 *      size : 'invisible',
 *      callback : (token)=>{ ... },
 *      expiredCallback : ()=>{ ... },
 *      errorCallback : (error)=>{ ... },
 *      badge: 'bottomleft',
 *      isolated : false
 *      inputForm : document.querySelector('#inputCaptchaField'),
 * });
 * 
 * // get response via method "getResponse" (alternative option instead using callback)
 * invisible_captchaV2.getResponse();
 * 
 * // reset google captcha
 * chkCaptchaV2.reset();
 * 
 * // has response method
 * chkCaptchaV2.hasResponse();
 *  
 * // get token
 * const tokenValue1 = chkCaptchaV2.CaptchaToken;
 * const tokenValue2 = invisible_captchaV2.CaptchaToken;
 */
class GrecaptchaV2_JS {
    /**
     * @param {Object} pGrecaptcha grecaptcha object.
     * @param {GRECAPTCHA_OPTIONS} pOptions
     * @constructor
     */
    constructor(pGrecaptcha, pOptions) {

        if (!pGrecaptcha) {
            throw new Error('Missing grecaptcha object.');
        }
        /**
         * @type {GRECAPTCHA_OPTIONS} 
         */
        let objOptions = {
            container: null,
            siteKey: null,
            theme: null,
            size: null,
            tabindex: null,
            callback: null,
            expiredCallback: null,
            errorCallback: null,
            badge: null,
            isolated: false,
            inputForm: null
        }

        Object.assign(objOptions, pOptions);

        if (!objOptions.selector || !objOptions.siteKey) {
            throw new Error('Missing params: selector or sitekey for Google grecaptcha');
        }

        /**
         * Indicate if recaptcha is invisible.
         * @type {Boolean}
         * @public
         */
        this.IsInvisible = objOptions?.size === 'invisible';
        /**
         * Html input that stores recaptcha value.
         * @type {HTMLInputElement} 
         * @public
         */
        this._inputForm = !!objOptions.inputForm && (typeof objOptions.inputForm == 'string' ? document.querySelector(objOptions.inputForm) : objOptions.inputForm);
        /**
         * grecaptcha object.
         * @type {Object} 
         * @private
         */
        this._grecaptcha = pGrecaptcha;
        /**
         * Recaptcha reponse token.
         * @type {String} 
         * @private
         */
        this._captchaToken = '';
        /**
         * Widget Id.
         * @type {Number} 
         * @private
         */
        this.widget_id = null;
        /**
         * Call back function that receives response token.
         * @type {Function} 
         * @public
         */
        this._fnCallback = typeof objOptions.callback == 'function' ? objOptions.callback : false;
        /**
         * Call back function that executes when captcha is expired.
         * @type {Function} 
         * @public
         */
        this._fnExpiredCallback = typeof objOptions.expiredCallback == 'function' ?  objOptions.expiredCallback : false;

        // Initialize recaptcha.
        this.init(objOptions);
    }
    // #region PUBLIC METHODS
    /**
     * Initialize method.
     * @param {GRECAPTCHA_OPTIONS} pObjOptions GrecaptchaV2_JS options.
     * @private
     */
    init(pObjOptions) {
        if (this.widget_id) return;

        if (this.IsInvisible) {
            this.widget_id = this._grecaptcha.render(pObjOptions.selector, {
                'sitekey': pObjOptions.siteKey,
                'badge': !!pObjOptions.badge ? pObjOptions.badge : 'bottomright',
                'size': 'invisible',
                'tabindex': !!pObjOptions.tabindex ? pObjOptions.tabindex : 0,
                'callback': this.responseCallback.bind(this),
                'expired-callback': this.expiredCallBack.bind(this),
                'error-Recaptchacallback': typeof pObjOptions?.errorCallback == 'function' ? pObjOptions.errorCallback : false,
                'isolated': pObjOptions.isolated
            });
        } else {
            this.widget_id = this._grecaptcha.render(pObjOptions.selector, {
                'sitekey': pObjOptions.siteKey,
                'theme': !!pObjOptions.theme ? pObjOptions.theme : 'light',
                'size': !!pObjOptions.size ? pObjOptions.size : 'normal',
                'tabindex': !!pObjOptions.tabindex ? pObjOptions.tabindex : 0,
                'callback': this.responseCallback.bind(this),
                'expired-callback': this.expiredCallBack.bind(this),
                'error-callback': typeof pObjOptions?.errorCallback == 'function' ? pObjOptions.errorCallback : false
            });
        }
    }

    /**
     * Every time captcha is resolved, google token is updated and executes _fnCallback.
     * @private
     * @param {String} ptoken google response token
     */
    responseCallback(ptoken) {
        this.CaptchaToken = ptoken;
        if (!!this._fnCallback) {
            this._fnCallback(ptoken);
        }
    }
    /**
     * If captcha is expired then reset captcha UI and input field. Afterwards, It executes _fnExpiredCallback.
     * @private
     */
    expiredCallBack() {
        this.reset();
        this._inputForm.classList.remove('is-valid');
        this._inputForm.classList.add('is-invalid');
        if (!!this._fnExpiredCallback) {
            this._fnExpiredCallback();
        }
    }
    /**
     * Compute google captcha response value.
     */
    getResponse() {
        if (this.IsInvisible) {
            this._grecaptcha.execute(this.widget_id);
        } else {
            this.CaptchaToken = this._grecaptcha.getResponse(this.widget_id);
        }
    }
    /**
     * Reset captcha UI.
     */
    reset() {
        this._grecaptcha.reset(this.widget_id);
        this.CaptchaToken = '';
    }
    /**
     * True if captcha UI has token value.
     * @returns {boolean} True if captcha has value.
     */
    hasResponse() {
        return this.CaptchaToken.length > 0;
    }
    // #endregion
    // #region PUBLIC PROPERTIES
    /**
     * Set captcha token.
     * @type {string}
     * @public 
     */
    set CaptchaToken(pValue) {
        this._captchaToken = pValue;
        //set input field with recaptcha value. 
        if (this._inputForm instanceof HTMLInputElement) {
            this._inputForm.value = pValue;
            this._inputForm.classList.remove('is-invalid');
            this._inputForm.classList.add('is-valid');
            let errLabel =  this._inputForm.parentElement.querySelector(`.invalid-feedback[data-valmsg-for="${this._inputForm.name}"]`);
            !!errLabel && (errLabel.innerHTML = '');
        }
    }
    /**
     * Get current captcha token.
     * @type {string} 
     * @public
     */
    get CaptchaToken() {
        return this._captchaToken;
    }
    // #endregion
    //#region STATIC METHODS
    /**
     * @description Add Google Recaptcha library to the DOM.
     * @param {Function} pFnOnload - Executes after Google has been loaded.
     * @param {Boolean} pIsAsync - True for enable async attribute.
     * @param {Boolean} pIsDefer - True for enable defer attribute.
     */
    static addRecaptchaLibrary(pFnOnload, pIsAsync = false, pIsDefer = false) {
        const _HTMLScript = document.createElement('SCRIPT');
        _HTMLScript.async = pIsAsync;
        _HTMLScript.defer = pIsDefer;

        if (!!pFnOnload && typeof pFnOnload == 'function') {
            _HTMLScript.src = `https://www.google.com/recaptcha/api.js?onload=${pFnOnload.name}&render=explicit`;
        } else {
            _HTMLScript.src = 'https://www.google.com/recaptcha/api.js';
        }

        document.getElementsByTagName('head')[0].appendChild(_HTMLScript);
    }
}
